import React, { useEffect, useState } from 'react';
import { PageHeader } from 'antd';
import { DndProvider } from 'react-dnd';
import { observer } from 'mobx-react';
import {
  academic_calendar_itemQuery,
  academic_calendar_itemQueryVariables,
  ACADEMIC_CALENDAR_ITEMS,
  locationQuery,
  locationQueryVariables,
  LOCATIONS,
  order_by,
  SUBJECTS_FOR_TIMETABLE,
  subjects_for_timetableQuery,
  subjects_for_timetableQueryVariables,
} from '@app-types/graphql-types';
import {
  TimelineConfiguration,
  TimelineItemConfigModal,
  TimetableActions,
  TimetableBlocks,
  TimetableConfiguration,
  timetableTourSteps,
} from '@modules/timetable';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useMst } from '@models/Root';
import { HSMSPageHeaderWrapper, PageBreadcrumbs } from '@components/shared';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { AppTours } from '@modules/app';

export const ManageTimetable = observer(() => {
  const [courseId, setCourseId] = useState<string>('');
  const [courseSemesterId, setCourseSemesterId] = useState<string>('');
  const { clearState } = useMst().timetable;

  // Clear the mobx timetable state at mount and unmount
  useEffect(() => {
    clearState();
    return () => {
      clearState();
    };
  }, [clearState]);

  // Get subject data
  const [getSubjects, { data: subjectsData, loading: subjectsLoading }] = useLazyQuery<
    subjects_for_timetableQuery,
    subjects_for_timetableQueryVariables
  >(SUBJECTS_FOR_TIMETABLE, {
    fetchPolicy: 'no-cache',
  });

  // Get location data
  const { data: locationData, loading: locationDataLoading } = useQuery<locationQuery, locationQueryVariables>(
    LOCATIONS,
    {
      variables: {
        order_by: [
          {
            name: order_by.asc,
          },
        ],
      },
      fetchPolicy: 'no-cache',
    },
  );

  // Get academic calendar data
  const [getAcademicCalendarItems, { data: academicCalendarItems }] = useLazyQuery<
    academic_calendar_itemQuery,
    academic_calendar_itemQueryVariables
  >(ACADEMIC_CALENDAR_ITEMS, {
    fetchPolicy: 'no-cache',
  });

  return (
    <div>
      <HSMSPageHeaderWrapper>
        <PageHeader
          title="Timetable"
          subTitle="Manage timetable"
          extra={
            <TimetableActions
              academicCalendarItems={academicCalendarItems?.academic_calendar_item}
              batchSubjectConfigurations={subjectsData?.batch_subject_configuration}
              subjectsData={subjectsData}
            />
          }
        />
      </HSMSPageHeaderWrapper>

      <PageBreadcrumbs
        paths={[
          {
            pathName: 'Timetable',
            url: '/timetable',
          },
          {
            pathName: 'Edit Timetable',
            url: '/timetable/edit',
          },
        ]}
      />

      <AppTours steps={timetableTourSteps.editTimetable} />

      <TimetableConfiguration
        courseId={courseId}
        setCourseId={setCourseId}
        courseSemesterId={courseSemesterId}
        setCourseSemesterId={setCourseSemesterId}
        getSubjects={getSubjects}
        getAcademicCalendarItems={getAcademicCalendarItems}
        subjectData={subjectsData}
      />

      <DndProvider backend={HTML5Backend}>
        <TimetableBlocks
          subjectsLoading={subjectsLoading}
          batchSubjects={subjectsData?.batch_subject_configuration || []}
          locationDataLoading={locationDataLoading}
          locationData={locationData?.location || []}
        />

        <TimelineConfiguration academicCalendarItems={academicCalendarItems?.academic_calendar_item} />
      </DndProvider>
      <TimelineItemConfigModal />
    </div>
  );
});
